import axios from "axios";
import { buildStaticOptions} from "../utils/misc";
import { getHeadersConfig } from "../utils/config";
import ErrorHandler from "../utils/ErrorHandler";
import {PageState,ResponseStatus,PlacementResponseDto,PlacementRecord,ErrorData, PageName, CampaignSourceRecord, CLinkOwnerOption, CLinkOwnerResponseDto, CLinkRecord} from "../utils/types";
import StateHelper from "../utils/StateHelper";
import {CampaignRecord,ProductRecord,ProductsCampaignsDto} from "../utils/types";
import dayjs from "../utils/dayjs";
import { joinURLs } from "../utils/urlUtil";

let abortControllerForSearchPlacements = new AbortController();
let abortControllerForSearchCampaigns= new AbortController();
let abortControllerForSearchCLinkOwners = new AbortController();

export const CommonRequests = {
  searchPlacements: function (q : string, setPlacementsResponseDto : (placementsResponseDto:PlacementResponseDto)=>void, setPlacementsIsLoading: (placementsIsLoading:boolean)=>void): void {
    if (abortControllerForSearchPlacements) abortControllerForSearchPlacements.abort();
    abortControllerForSearchPlacements = new AbortController();

    setPlacementsIsLoading(true);

    let publishersAndSites = "";
    const pageState : PageState = StateHelper.getPageState();

    if (pageState.publishers != null && pageState.publishers.length > 0) {
      publishersAndSites = pageState.publishers.join(",")
    }

    const params = {
      "placementsInclude": publishersAndSites,
      "q": q
    };

    const url = joinURLs("2/dashboard/placements", process.env.REACT_APP_REPORTING_API_BASE_URL);
    const config = getHeadersConfig();

    const staticOptions = buildStaticOptions( q );

    const dto : PlacementResponseDto = {
      status : ResponseStatus.OK, 
      error : null,
      placementsResponse : staticOptions
    };
    setPlacementsResponseDto(dto);

    axios
      .get(url, {...config, signal: abortControllerForSearchPlacements.signal, params: params})
      .then(function (response: any) {
        let options: PlacementRecord[] = response.data.placements;
        
        // add the static search items to the start of the options returned from the server
        options = [...staticOptions, ...options];

        const dto : PlacementResponseDto = {
          status : ResponseStatus.OK, 
          error : null,
          placementsResponse : options
        };

        setPlacementsResponseDto(dto);
        setPlacementsIsLoading(false);
      })
      .catch(function (error : any) {
        if (!axios.isCancel(error)) {
          setPlacementsIsLoading(false);

          const isLoggedOut : boolean = ErrorHandler.handleLoggedOutError(error);
          if(! isLoggedOut){
            const errData : ErrorData = ErrorHandler.getErrorMessage(error);
            
            const errorItem: PlacementRecord = {} as PlacementRecord
            errorItem.apptapPublisherId = "ERROR";
            errorItem.apptapSiteId = "ERROR";
            errorItem.publisherPlacementId = "ERROR";
            errorItem.publisherPlacementName = "Unable to load Publisher Placements";

            const options: PlacementRecord[] = [...staticOptions, errorItem];

            const dto : PlacementResponseDto = {
              status : ResponseStatus.ERROR, 
              error : errData,
              placementsResponse : options
            };
            setPlacementsResponseDto(dto);
          }
        }
      });
  },

  loadProductsAndCampaigns : async(page: string, date: dayjs.Dayjs, returnProductsAndCampaigns: boolean) => {
    if (abortControllerForSearchCampaigns) abortControllerForSearchCampaigns.abort();
    abortControllerForSearchCampaigns = new AbortController();

    const pagePartialUrl = page === PageName.products ? "dashboard" : "performance"; 
    const config = getHeadersConfig();
    let dto : ProductsCampaignsDto;

    try {
      const url = joinURLs("2/" + pagePartialUrl + "/products-campaigns", process.env.REACT_APP_REPORTING_API_BASE_URL);
      const response : any = await axios.get(url, { 
        params: {
          "dateStart": dayjs.utc(date).format("YYYY-MM-DD"),
          "returnProductsAndCampaigns": returnProductsAndCampaigns
        },
        ...config, 
        signal: abortControllerForSearchCampaigns.signal 
      });
      const prodResponse : ProductRecord[] = ( response.data.products as ProductRecord[] );
      const campaignSourcesResponse : CampaignSourceRecord[] = ( response.data.campaignSources as CampaignRecord[] );
      let campResponse : CampaignRecord[] = ( response.data.campaigns as CampaignRecord[] );

      if (!campResponse) campResponse = [];
      campaignSourcesResponse.forEach((campaignSourceRecord:CampaignSourceRecord) => {
        if (campaignSourceRecord.campaignSource || campaignSourceRecord.campaignSourceAlias) {
          const ret:CampaignRecord = {} as CampaignRecord;
          ret.campaignSource = campaignSourceRecord.campaignSource;
          ret.campaignSourceAlias = campaignSourceRecord.campaignSourceAlias ? campaignSourceRecord.campaignSourceAlias : campaignSourceRecord.campaignSourceHashID;
          ret.campaignSourceHashID = campaignSourceRecord.campaignSourceHashID;
          ret.campaignId = "***";
          ret.name = campaignSourceRecord.campaignSourceAlias = ": All Campaigns";
          campResponse.push(ret);
        }
      });
      // computed prop to use like labelKey on autocomplete
      campResponse.forEach((campaign:CampaignRecord) => campaign.labelKey = (campaign.campaignSourceAlias == null ? "***" : campaign.campaignSourceAlias) + ":" + (campaign.campaignId == null ? "***" : campaign.campaignId));
      campResponse.sort((a:CampaignRecord, b:CampaignRecord) => (a.campaignSource + ":" + a.campaignId).localeCompare(b.campaignSource + ":" + b.campaignId));

      if (prodResponse) {
        prodResponse.sort((a,b) => a.productId == null ? -1 : a.productId.localeCompare(b.productId));
      }

      dto = {
        status : ResponseStatus.OK, 
        error : null,
        productsResponse : prodResponse,
        campaignsResponse : campResponse
      };
    } catch (error) {
      const isLoggedOut : boolean = ErrorHandler.handleLoggedOutError(error);
      if(! isLoggedOut){
        const errData : ErrorData = ErrorHandler.getErrorMessage(error);
        dto = {
          status : ResponseStatus.ERROR, 
          error : errData,
          productsResponse : null,
          campaignsResponse : null,
        };
      }
    }

    return dto;
  },

  searchCLInkOwners: async (q : String) => {
    if (abortControllerForSearchCLinkOwners) abortControllerForSearchCLinkOwners.abort();
    abortControllerForSearchCLinkOwners = new AbortController();

    const params = { q };
    const config = getHeadersConfig();
    let dto : CLinkOwnerResponseDto;

    try {
      const url = joinURLs("2/dashboard/clinks", process.env.REACT_APP_REPORTING_API_BASE_URL);
      const response : any = await axios.get(url, { ...config, signal: abortControllerForSearchCLinkOwners.signal, params });

      const owners:string[] = response.data.owners;
      const cLinks = response.data.cLinks;
      const cLinkArray : (CLinkOwnerOption)[] = [];

      if (owners && owners.length) {
        owners.forEach((owner:string) => {
          
          const cLinkOption: CLinkOwnerOption = Object();
          cLinkOption.key = owner;
          cLinkOption.id = owner;
          cLinkOption.record = owner;
          cLinkArray.push(cLinkOption);
          cLinks.forEach((cLinkRecord:CLinkRecord) => {
            if (cLinkRecord.owner !== owner) return;
            const cLinkOption: CLinkOwnerOption = Object();
            cLinkOption.key = owner;
            cLinkOption.id = owner;
            cLinkOption.record = cLinkRecord;
            cLinkArray.push(cLinkOption);
          });
        });
      } else {
        cLinks.forEach((cLinkRecord:CLinkRecord) => {
          const cLinkOption: CLinkOwnerOption = Object();
          cLinkOption.key = cLinkRecord.cLinkId;
          cLinkOption.id = cLinkRecord.cLinkId;
          cLinkOption.record = cLinkRecord;
          cLinkArray.push(cLinkOption);
        });
      }

      dto = {
        status : ResponseStatus.OK, 
        error : null,
        cLinksOwnerResponse : cLinkArray
      };
    } catch (error) {
      if (!axios.isCancel(error)) {
        const isLoggedOut : boolean = ErrorHandler.handleLoggedOutError(error);
        if (!isLoggedOut) {
          const errData : ErrorData = ErrorHandler.getErrorMessage(error);
          dto = {
            status : ResponseStatus.ERROR, 
            error : errData,
            cLinksOwnerResponse : null
          };
        }
      }
    }
    return dto;
  }
};